import React, { useEffect, useState } from "react";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useProperties } from "../context/PropertyContext";
import PropertySelector from "../components/PropertySelector";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, BarChart3, Download } from "lucide-react";

const PERIODS = ["day", "week", "month", "year"];

export default function Reports() {
  const { t } = useLang();
  const { format: money } = useCurrency();
  const { currentPropertyId, properties } = useProperties();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [comp, setComp] = useState(null);

  const load = async (p) => {
    try {
      const params = { period: p };
      if (currentPropertyId) params.property_id = currentPropertyId;
      const { data } = await api.get(`/reports/summary`, { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period, currentPropertyId]);

  useEffect(() => {
    const params = {};
    if (currentPropertyId) params.property_id = currentPropertyId;
    api.get("/reports/comparison", { params }).then((r) => setComp(r.data)).catch(() => {});
  }, [currentPropertyId]);

  const handleExport = async () => {
    try {
      const params = { period };
      if (currentPropertyId) params.property_id = currentPropertyId;
      const res = await api.get(`/reports/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aqar-report-${period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
            <BarChart3 className="text-[#2A5948]" size={26} />
            {t("reports")}
          </h1>
          <p className="text-sm text-[#5C6053] mt-1">{data && `${data.start} → ${data.end}`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {properties.length > 1 && <PropertySelector />}
          <button
            data-testid="export-csv-btn"
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E1D8] bg-white text-sm hover:bg-[#F3EFE7]"
          >
            <Download size={14} /> {t("export_csv")}
          </button>
          <div className="flex gap-1 p-1 bg-white border border-[#E5E1D8] rounded-xl">
            {PERIODS.map((p) => (
              <button
                key={p}
                data-testid={`period-btn-${p}`}
                onClick={() => setPeriod(p)}
                className={`px-3 py-2 text-sm rounded-lg ${period === p ? "bg-[#2A5948] text-white" : "text-[#1C1E17] hover:bg-[#F3EFE7]"}`}
              >
                {t(`period_${p}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label={t("income")} value={data?.income} tone="green" testid="report-income" />
        <Stat label={t("expense")} value={data?.expense} tone="red" testid="report-expense" />
        <Stat label={t("profit")} value={data?.profit} tone="brand" testid="report-profit" />
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4">{t("income")} / {t("expense")}</h3>
        <div className="w-full h-72">
          <ResponsiveContainer>
            <BarChart data={data?.breakdown || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D8" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar name={t("income")} dataKey="income" fill="#2A5948" radius={[4, 4, 0, 0]} />
              <Bar name={t("expense")} dataKey="expense" fill="#C96A52" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4">{t("profit")}</h3>
        <div className="w-full h-56">
          <ResponsiveContainer>
            <LineChart data={(data?.breakdown || []).map((d) => ({ ...d, profit: d.income - d.expense }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D8" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="profit" stroke="#C96A52" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Weekly & monthly comparison */}
      {comp && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ComparisonCard
            testid="comparison-week"
            title={t("comparison")}
            current={comp.week.current}
            previous={comp.week.previous}
            curLabel={t("this_week")}
            prevLabel={t("last_week")}
          />
          <ComparisonCard
            testid="comparison-month"
            title={t("comparison")}
            current={comp.month.current}
            previous={comp.month.previous}
            curLabel={t("this_month")}
            prevLabel={t("last_month")}
          />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const { format: money } = useCurrency();
  const map = {
    green: "bg-[#E6F1E7] text-[#2A5948]",
    red: "bg-[#FBE9E4] text-[#A85540]",
    brand: "bg-[#2A5948] text-white",
  };
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm">
      <div className={`text-xs px-2 py-1 rounded inline-block ${map[tone]}`}>{label}</div>
      <div className="mt-3 text-2xl font-semibold">{value != null ? money(value) : "—"}</div>
    </div>
  );
}

function ComparisonCard({ title, current, previous, curLabel, prevLabel, testid }) {
  const { t } = useLang();
  const chartData = [
    { key: prevLabel, income: previous.income, expense: previous.expense, profit: previous.profit },
    { key: curLabel, income: current.income, expense: current.expense, profit: current.profit },
  ];
  const diff = (current.profit || 0) - (previous.profit || 0);
  const pct = previous.profit ? ((diff / Math.abs(previous.profit)) * 100).toFixed(1) : (current.profit ? "100.0" : "0.0");
  const up = diff >= 0;
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">{curLabel} vs {prevLabel}</h3>
          <p className="text-xs text-[#5C6053] mt-1">{t("growth")}</p>
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${up ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FBE9E4] text-[#A85540]"}`}>
          {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          <span>{pct}%</span>
        </div>
      </div>
      <div className="w-full h-48 mt-3">
        <ResponsiveContainer>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D8" />
            <XAxis dataKey="key" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar name={t("income")} dataKey="income" fill="#2A5948" radius={[4, 4, 0, 0]} />
            <Bar name={t("expense")} dataKey="expense" fill="#C96A52" radius={[4, 4, 0, 0]} />
            <Bar name={t("profit")} dataKey="profit" fill="#E5B25D" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
